import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { MessageSquare, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/groups`);
      setGroups(response.data);
    } catch (error) {
      toast.error('Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="groups-page" className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground tracking-tight">GROUPS</h1>
        <p className="text-muted-foreground mt-2 font-body">All monitored WhatsApp groups</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-card border border-border p-6 animate-pulse">
              <div className="h-20 bg-muted rounded"></div>
            </Card>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <Card className="bg-card border border-border p-12 text-center">
          <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No groups found. Connect WhatsApp and sync groups to get started!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {groups.map((group) => (
            <Card key={group.group_id} data-testid={`group-card-${group.group_id}`} className="bg-card border border-border p-6 hover:border-primary/50 transition-colors duration-300">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6 text-primary" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        {group.group_name || 'Unknown Group'}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {group.participants && (
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{group.participants.length} participants</span>
                          </div>
                        )}
                        {group.last_activity && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>
                              Last activity {formatDistanceToNow(new Date(group.last_activity), { addSuffix: true })}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="mt-2">
                        <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                          {group.group_id}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-md text-xs font-medium ${
                  group.is_active 
                    ? 'bg-green-500/10 text-green-500' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {group.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Groups;
